/**
 * The Allocation calendar's resource lane roster, loaded from the contact roster rather than derived
 * from the allocations in view.
 *
 * A scheduler board answers "who is free", so the axis has to carry people with NOTHING booked. The
 * board's own derivation reads lanes off the records it fetched, which can only ever produce lanes
 * that already have work — an empty week collapses the axis to a single nameless column. So
 * Allocation supplies a `resourceLoader`: it asks for the employee and contractor rosters directly,
 * and the lanes stand whether or not anyone booked against them, matching the legacy scheduler.
 *
 * Mapping is pure and separate from the fetch on purpose: which lanes exist, which are hidden and
 * how they nest is the part that silently loses events when it is wrong, so it is testable without
 * HTTP.
 */
import { ActivityEntityName, ContactType, EntityCalendarResourceType, getContactTypeMembershipForSystemTeams, isContactTeamMember, isSystemTeamId, UNPAGED_MAX_RECORDS, UnallocatedUserId } from '@jms/model';
import type { Contact, Entity, EntityCalendarResource, EntityViewCalendarResources, EntityViewFilter } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import type { EntityCalendarResourceLoaderContext } from '@workbuddy/services/entities/entity.manifest.calendar';
import { firstValueFrom } from 'rxjs';
import { isResourceView } from '@workbuddy/ui/v2/components/calendar.host';

/** Lane title for the catch-all bucket allocations with no resource fall into. */
const UNASSIGNED_LANE_TITLE = 'Unassigned';

/**
 * `extendedProps` key the default lane renderer draws through the resource field's own metadata
 * renderer, and the key `deriveCalendarResources` writes for a derived roster. Loaded lanes use the
 * same key so a loaded person renders identically to a derived one — avatar, suburb, scheduling tags
 * and timezone are all read off this ref, never off the lane.
 */
const RESOURCE_REF_PROP = 'ref';

/** Lane types this entity rosters, and the contact entity each is loaded from. */
const ALLOCATION_ROSTER_SOURCES: readonly { type: EntityCalendarResourceType; entity: ActivityEntityName; contactType: ContactType }[] = [
  { type: EntityCalendarResourceType.Employee, entity: ActivityEntityName.ContactEmployee, contactType: ContactType.Employee },
  { type: EntityCalendarResourceType.Contractor, entity: ActivityEntityName.Contractor, contactType: ContactType.Subcontractor },
];

/** One roster contact, narrowed to what a lane can print. */
export type AllocationRosterContact = Pick<Contact, '_id' | 'name' | 'type' | 'address' | 'scheduling' | 'workCategories' | 'parents'>;

/** The roster for one lane type, paired with the lane type its contacts get. */
export interface AllocationRoster {
  type: EntityCalendarResourceType;
  contacts: AllocationRosterContact[];
}

/**
 * Loads the employee and contractor rosters and maps them onto lanes.
 *
 * A hidden lane type is not fetched at all — `lanes[type].show === false` is the view saying those
 * people are not on this calendar, so asking the server for them would be a round trip whose answer
 * is thrown away.
 */
export async function loadAllocationCalendarResources(context: EntityCalendarResourceLoaderContext): Promise<EntityCalendarResource[]> {

  const resources = context.calendar?.resources;
  if (!isResourceView(context.calendar?.view) || resources?.field !== 'resource') return [];

  const entityData = context.injector.get(EntityDataServiceRaw);
  const lanes = resources?.lanes;

  const wanted = ALLOCATION_ROSTER_SOURCES.filter(source => lanes?.[source.type]?.show !== false);
  const rosters = await Promise.all(wanted.map(async source => {
    const filters = rosterFilters(resources, source.contactType, lanes?.[source.type]?.restrictTo);
    return { type: source.type, contacts: filters === null ? [] : await readRoster(entityData, source.entity, filters) };
  }));

  return mapAllocationCalendarResources(rosters, resources);
}

/**
 * Turns loaded rosters into lanes.
 *
 * Lane ids are the contact `_id` because that is exactly what an allocation's `resource` ref holds,
 * so an event's `resourceIds` land on the lane the roster produced. `type` is set explicitly rather
 * than left to be sniffed off the ref: a contractor contact carries `ContactType.Subcontractor`,
 * which is not a lane type, so a lane that relied on the ref would fall out of `lanes[type]` policy
 * entirely.
 *
 * The roster comes back FLAT even when the view sets `groupBy`. Nesting is the board's job and it
 * applies the same `nestCalendarResources` to a loaded roster and a derived one, so a loader that
 * grouped here would have to re-implement the header shape — which is exactly how the two drifted.
 */
function mapAllocationCalendarResources(rosters: AllocationRoster[], resources: EntityViewCalendarResources | null | undefined): EntityCalendarResource[] {

  const lanes = resources?.lanes;
  const mapped: EntityCalendarResource[] = [];

  for (const roster of rosters) {
    // Re-checked here as well as at the fetch so the mapping alone honours lane policy: a caller
    // that hands over a roster for a hidden type still gets no lanes for it.
    if (lanes?.[roster.type]?.show === false) continue;

    const categories = lanes?.[roster.type]?.restrictTo;

    for (const contact of roster.contacts ?? []) {
      if (!contact?._id) continue;
      // Match the legacy scheduler's roster: only contacts explicitly enabled for scheduling are
      // resources. Keep an omitted scheduling projection as unknown rather than treating missing
      // data as disabled; this loader requests the field, but generic/projected callers may not.
      if (!isSchedulable(contact)) continue;
      if (!matchesWorkCategories(contact, categories)) continue;
      if (resources?.selection?.mode === 'named' && !resources.selection.ids.includes(contact._id)) continue;
      if (resources?.selection?.mode === 'teams' && !resources.selection.ids.some(id => isContactTeamMember(contact, id))) continue;

      mapped.push({
        id: contact._id,
        title: contact.name ?? contact._id,
        type: roster.type,
        extendedProps: { [RESOURCE_REF_PROP]: contact },
      });
    }
  }

  mapped.sort((left, right) => left.title.localeCompare(right.title));

  // Unassigned work stays visible before the named roster and any group headers.
  if (lanes?.[EntityCalendarResourceType.Unassigned]?.show !== false) {
    mapped.unshift({ id: UnallocatedUserId, title: UNASSIGNED_LANE_TITLE, type: EntityCalendarResourceType.Unassigned, order: -1 });
  }

  return mapped;
}

/** Missing projection is unknown/keep; a present scheduling value must explicitly opt in. */
function isSchedulable(contact: AllocationRosterContact): boolean {

  return contact.scheduling === undefined || contact.scheduling?.enabled === true;
}

/**
 * `lanes[type].restrictTo`: only resources carrying one of the named work categories keep their lane.
 *
 * Contacts without a matching category do not satisfy an explicit restriction.
 */
function matchesWorkCategories(contact: AllocationRosterContact, categories: Entity[] | undefined): boolean {

  if (!categories?.length) return true;

  const own = contact.workCategories ?? [];
  if (!own.length) return false;

  const wanted = new Set(categories.map(category => category?._id).filter((id): id is string => !!id));

  return own.some(category => !!category?._id && wanted.has(category._id));
}

/**
 * One roster fetch: every active contact of one kind, unpaged and unprojected.
 *
 * Paged explicitly rather than relying on `unpaged` alone — see the note at the request below.
 *
 * `skipProjection` because the lane renderer reads suburb, scheduling tags and timezone straight off
 * the ref, and the view's own projection is built for allocation columns — it would not carry any of
 * them.
 */
async function readRoster(entityData: EntityDataServiceRaw, entity: ActivityEntityName, filters: EntityViewFilter[]): Promise<AllocationRosterContact[]> {

  // No try/catch: a failed roster fetch must reach the board, which falls back to deriving lanes from
  // the booked records. Swallowing it here produced an empty roster the board trusted — a blank axis.
  // Active-only filtering is the strategy's default. Selection and scheduling are narrowed on the
  // server so a named resource or team never requires downloading the tenant's complete roster.
  // An explicit page accompanies `unpaged` because the contact strategy IGNORES `unpaged` and falls
  // back to its default page of 50 — a tenant with 192 employees got 50 lanes and nothing to say the
  // rest existed. `UNPAGED_MAX_RECORDS` is the cap the platform already uses for an unpaged read, so
  // the roster stays bounded and no paging loop is needed: a contact roster past 5000 is not an axis
  // anyone can read.
  const response = await firstValueFrom(entityData.getAllV2(entity, { filters, unpaged: true, page: 0, pageSize: UNPAGED_MAX_RECORDS, skipProjection: true }));

  return (response?.items ?? []) as AllocationRosterContact[];
}

/** Null means no contacts of this type were selected, so no request should be sent. */
function rosterFilters(resources: EntityViewCalendarResources, contactType: ContactType, categories?: Entity[]): EntityViewFilter[] | null {

  const filters: EntityViewFilter[] = [{ _id: 'scheduling.enabled', value: true }];
  if (categories?.length) filters.push({ _id: 'workCategories._id', value: categories.map(category => category._id) });
  const selection = resources.selection;
  if (!selection) return filters;
  const ids = [...new Set(selection.ids.filter(Boolean))];
  if (!ids.length) return null;

  if (selection.mode === 'named') return [...filters, { _id: '_id', value: ids }];

  // CRM's virtual teams are defined by contact type, while ordinary teams are parent refs. If a
  // virtual team includes this whole contact type, additional ordinary teams cannot widen it.
  if (getContactTypeMembershipForSystemTeams(ids).includes(contactType)) return filters;
  const teamIds = ids.filter(id => !isSystemTeamId(id));
  return teamIds.length ? [...filters, { _id: 'parents._id', value: teamIds }] : null;
}
